import { useState, useCallback, useEffect } from 'react'
import type { OnboardingState, UseOnboardingStateReturn } from '@/types'

const STORAGE_KEY = 'ledger_onboarding'

interface StoredOnboardingState {
  hasCompletedOnboarding: boolean
  completedSteps: string[]
  lastVisit: string | null
}

function getStoredState(): OnboardingState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed: StoredOnboardingState = JSON.parse(stored)
      return {
        hasCompletedOnboarding: parsed.hasCompletedOnboarding,
        completedSteps: parsed.completedSteps,
        lastVisit: parsed.lastVisit ? new Date(parsed.lastVisit) : null,
      }
    }
  } catch {
    // Invalid stored state, return default
  }
  return {
    hasCompletedOnboarding: false,
    completedSteps: [],
    lastVisit: null,
  }
}

function saveState(state: OnboardingState): void {
  try {
    const toStore: StoredOnboardingState = {
      hasCompletedOnboarding: state.hasCompletedOnboarding,
      completedSteps: state.completedSteps,
      lastVisit: state.lastVisit?.toISOString() ?? null,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
  } catch {
    // Storage not available, ignore
  }
}

/**
 * Hook for managing onboarding state
 * Persists to localStorage under 'ledger_onboarding'
 */
export function useOnboardingState(): UseOnboardingStateReturn {
  const [state, setState] = useState<OnboardingState>(getStoredState)

  // Update lastVisit on mount
  useEffect(() => {
    setState((prev) => {
      const newState = { ...prev, lastVisit: new Date() }
      saveState(newState)
      return newState
    })
  }, [])

  const completeOnboarding = useCallback(() => {
    setState((prev) => {
      const newState: OnboardingState = {
        ...prev,
        hasCompletedOnboarding: true,
        lastVisit: new Date(),
      }
      saveState(newState)
      return newState
    })
  }, [])

  const completeStep = useCallback((stepId: string) => {
    setState((prev) => {
      if (prev.completedSteps.includes(stepId)) {
        return prev
      }
      const newState: OnboardingState = {
        ...prev,
        completedSteps: [...prev.completedSteps, stepId],
        lastVisit: new Date(),
      }
      saveState(newState)
      return newState
    })
  }, [])

  const resetOnboarding = useCallback(() => {
    const newState: OnboardingState = {
      hasCompletedOnboarding: false,
      completedSteps: [],
      lastVisit: new Date(),
    }
    setState(newState)
    saveState(newState)
  }, [])

  const isStepCompleted = useCallback(
    (stepId: string): boolean => {
      return state.completedSteps.includes(stepId)
    },
    [state.completedSteps]
  )

  return {
    state,
    completeOnboarding,
    completeStep,
    resetOnboarding,
    isStepCompleted,
  }
}
